import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, ShoppingBag, User, LogOut, Menu, X, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';

const Navigation = () => {
  const { user, logout } = useAuth();
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [navCategories, setNavCategories] = useState([]);
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);
  const cartCount = getCartCount();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Fetch dynamic categories
  useEffect(() => {
    api.get('/categories').then(r => setNavCategories(r.data || [])).catch(() => {});
  }, []);

  // Close search on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); setSearchOpen(false); setSearchResults([]); setSearchQuery(''); }, [location.pathname]);

  // Debounced search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.get('/products/search', { params: { q: searchQuery } });
        setSearchResults(res.data || []);
      } catch { setSearchResults([]); }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  const handleSearchSelect = (productId) => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    navigate(`/product/${productId}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearchOpen(false);
    setSearchResults([]);
    navigate(`/products?q=${encodeURIComponent(searchQuery.trim())}`);
    setSearchQuery('');
  };

  const isActive = (path) => {
    if (path.includes('?')) {
      const [pathname, search] = path.split('?');
      return location.pathname === pathname && location.search.includes(search);
    }
    return location.pathname === path;
  };

  const navLinkClass = (path) =>
    `text-sm uppercase tracking-[0.2em] transition-colors ${
      isActive(path)
        ? 'text-gold border-b-2 border-gold pb-0.5 font-medium'
        : 'text-forest hover:text-gold'
    }`;

  const mobileLinkClass = (path) =>
    `block text-sm uppercase tracking-[0.2em] transition-colors ${
      isActive(path)
        ? 'text-gold font-medium pl-2 border-l-2 border-gold'
        : 'text-forest'
    }`;

  return (
    <nav className="sticky top-0 z-50 glass border-b border-[#1F3D2B]/10" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_draped-elegance-2/artifacts/eje53oty_Gemini_Generated_Image_loh71ploh71ploh7-removebg-preview.png" 
              alt="Vastram Logo" 
              className="h-14 w-auto object-contain logo-enhanced"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/#vastram-mirror" onClick={() => { setTimeout(() => { const el = document.getElementById('vastram-mirror'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 100); }} className={`${navLinkClass('/vastram-mirror')} flex items-center space-x-1`} data-testid="nav-mirror">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <span>Mirror</span>
            </Link>
            <Link to="/products?category=saree" className={navLinkClass('/products?category=saree')} data-testid="nav-sarees">Sarees</Link>
            <Link to="/products?category=suit" className={navLinkClass('/products?category=suit')} data-testid="nav-suits">Suits</Link>
            {navCategories.filter(c => !['saree', 'suit'].includes(c.slug)).map(c => (
              <Link key={c.category_id} to={`/products?category=${c.slug}`} className={navLinkClass(`/products?category=${c.slug}`)} data-testid={`nav-cat-${c.slug}`}>{c.name}</Link>
            ))}
            <Link to="/about" className={navLinkClass('/about')} data-testid="nav-about">Our Story</Link>
            <Link to="/drape-stories" className={navLinkClass('/drape-stories')} data-testid="nav-blog">Drape Stories</Link>
            <Link to="/contact" className={navLinkClass('/contact')} data-testid="nav-contact">Contact</Link>
          </div>

          {/* Icons */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative flex items-center" ref={searchRef}>
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="flex items-center justify-center text-forest hover:text-gold transition-colors"
                data-testid="nav-search-toggle"
              >
                <Search className="w-5 h-5" />
              </button>
              {/* Desktop search — inline expanding */}
              <AnimatePresence>
                {searchOpen && (
                  <motion.div
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 280 }}
                    exit={{ opacity: 0, width: 0 }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 overflow-hidden hidden md:block"
                  >
                    <form onSubmit={handleSearchSubmit} className="flex">
                      <input
                        autoFocus
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search sarees, suits..."
                        className="w-full px-4 py-2 text-sm bg-white border border-gold/40 rounded-l-full focus:outline-none focus:border-gold text-forest placeholder:text-forest/40"
                        data-testid="nav-search-input"
                      />
                      <button type="submit" className="bg-forest text-ivory px-3 py-2 rounded-r-full hover:bg-forest/90 transition-colors">
                        <Search className="w-4 h-4" />
                      </button>
                    </form>
                    {(searchResults.length > 0 || searchLoading) && (
                      <div className="absolute top-full mt-1 w-full bg-white border border-gold/30 shadow-xl rounded-sm max-h-80 overflow-y-auto z-50" data-testid="search-results-dropdown">
                        {searchLoading ? (
                          <p className="text-xs text-muted-foreground p-3">Searching...</p>
                        ) : (
                          searchResults.map(p => (
                            <button
                              key={p.product_id}
                              onClick={() => handleSearchSelect(p.product_id)}
                              className="flex items-center space-x-3 w-full p-3 hover:bg-neutral-warm transition-colors text-left"
                              data-testid={`search-result-${p.product_id}`}
                            >
                              <img src={p.images?.[0] || '/placeholder.jpg'} alt="" className="w-10 h-12 object-cover rounded-sm flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-forest font-medium truncate">{p.name}</p>
                                <p className="text-xs text-muted-foreground capitalize">{p.category} · {'\u20B9'}{p.price?.toLocaleString()}</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Mobile search — full-width centered overlay */}
              <AnimatePresence>
                {searchOpen && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] md:hidden"
                    data-testid="mobile-search-overlay"
                  >
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setSearchOpen(false); setSearchResults([]); }} />
                    <div className="relative z-10 pt-6 px-4">
                      <form onSubmit={handleSearchSubmit} className="flex shadow-xl rounded-full overflow-hidden">
                        <input
                          autoFocus
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search sarees, suits..."
                          className="flex-1 px-5 py-3.5 text-sm bg-white border-0 focus:outline-none text-forest placeholder:text-forest/40"
                          data-testid="mobile-search-overlay-input"
                        />
                        <button type="submit" className="bg-forest text-ivory px-5 py-3.5 hover:bg-forest/90 transition-colors">
                          <Search className="w-4 h-4" />
                        </button>
                      </form>
                      {(searchResults.length > 0 || searchLoading) && (
                        <div className="mt-2 bg-white border border-gold/30 shadow-xl rounded-sm max-h-72 overflow-y-auto" data-testid="mobile-search-results">
                          {searchLoading ? (
                            <p className="text-xs text-muted-foreground p-3">Searching...</p>
                          ) : (
                            searchResults.map(p => (
                              <button
                                key={p.product_id}
                                onClick={() => handleSearchSelect(p.product_id)}
                                className="flex items-center space-x-3 w-full p-3 hover:bg-neutral-warm transition-colors text-left"
                              >
                                <img src={p.images?.[0] || '/placeholder.jpg'} alt="" className="w-10 h-12 object-cover rounded-sm flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm text-forest font-medium truncate">{p.name}</p>
                                  <p className="text-xs text-muted-foreground capitalize">{p.category} · {'\u20B9'}{p.price?.toLocaleString()}</p>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {user && (
              <Link to="/wishlist" className="text-forest hover:text-gold transition-colors" data-testid="nav-wishlist">
                <Heart className="w-5 h-5" />
              </Link>
            )}
            
            <Link to="/cart" className="relative text-forest hover:text-gold transition-colors" data-testid="nav-cart">
              <ShoppingBag className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-forest text-ivory text-xs w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>

            {user ? (
              <div className="hidden md:flex items-center space-x-4">
                {user.is_admin && (
                  <Link to="/admin" className="text-sm uppercase tracking-[0.2em] text-forest hover:text-gold transition-colors" data-testid="nav-admin">
                    Admin
                  </Link>
                )}
                <Link to="/dashboard" className="text-forest hover:text-gold transition-colors" data-testid="nav-dashboard">
                  <User className="w-5 h-5" />
                </Link>
                <button onClick={handleLogout} className="text-forest hover:text-gold transition-colors" data-testid="nav-logout">
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <Link to="/login" className="hidden md:flex text-forest hover:text-gold transition-colors" data-testid="nav-login">
                <User className="w-5 h-5" />
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-forest"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-ivory border-t border-gold"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {/* Mobile Search */}
              <form onSubmit={(e) => { e.preventDefault(); if (searchQuery.trim()) { navigate(`/products?q=${encodeURIComponent(searchQuery.trim())}`); setSearchQuery(''); setMobileMenuOpen(false); } }}
                className="flex mb-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="flex-1 px-4 py-2.5 text-sm bg-white border border-gold/40 rounded-l-sm focus:outline-none focus:border-gold text-forest"
                  data-testid="mobile-search-input"
                />
                <button type="submit" className="bg-forest text-ivory px-4 py-2.5 rounded-r-sm">
                  <Search className="w-4 h-4" />
                </button>
              </form>

              <Link to="/#vastram-mirror" onClick={() => { setMobileMenuOpen(false); setTimeout(() => { const el = document.getElementById('vastram-mirror'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 100); }} className={mobileLinkClass('/vastram-mirror')}>Mirror</Link>
              <Link to="/products?category=saree" className={mobileLinkClass('/products?category=saree')} onClick={() => setMobileMenuOpen(false)}>Sarees</Link>
              <Link to="/products?category=suit" className={mobileLinkClass('/products?category=suit')} onClick={() => setMobileMenuOpen(false)}>Suits</Link>
              {navCategories.filter(c => !['saree', 'suit'].includes(c.slug)).map(c => (
                <Link key={c.category_id} to={`/products?category=${c.slug}`} className={mobileLinkClass(`/products?category=${c.slug}`)} onClick={() => setMobileMenuOpen(false)}>{c.name}</Link>
              ))}
              <Link to="/about" className={mobileLinkClass('/about')} onClick={() => setMobileMenuOpen(false)}>Our Story</Link>
              <Link to="/drape-stories" className={mobileLinkClass('/drape-stories')} onClick={() => setMobileMenuOpen(false)}>Drape Stories</Link>
              <Link to="/contact" className={mobileLinkClass('/contact')} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              
              {user ? (
                <>
                  {user.is_admin && (
                    <Link to="/admin" className="block text-sm uppercase tracking-[0.2em] text-forest" onClick={() => setMobileMenuOpen(false)}>Admin</Link>
                  )}
                  <Link to="/dashboard" className="block text-sm uppercase tracking-[0.2em] text-forest" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block text-sm uppercase tracking-[0.2em] text-forest text-left w-full">Logout</button>
                </>
              ) : (
                <Link to="/login" className="block text-sm uppercase tracking-[0.2em] text-forest" onClick={() => setMobileMenuOpen(false)}>Login</Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
